import { useCallback, useMemo, useState } from 'react';
import { RootState, useAppSelector } from '../../store.model';
import { getZipControlUnitsByDeviceIds } from '../selectors';
import { isEqual } from 'lodash';
import LocationAPI from '../../../service/locations/LocationAPI';
import { useParams } from 'react-router-dom';
import useGetDeviceFromStoreByParam from '../../../service/device/hooks/useGetDeviceFromStoreParam';
import { DevicesStoreInfoResponse } from '../../../service/store/StoreService.model';
import { fromBase64 } from '../../../shared/util/base64';
import ControlUnitAPI, { ControlUnitQueryArg } from '../ControlUnitAPI';
import useMultipleQueries from '../../../shared/util/rtk/useQueries';

interface Props {
  deviceId?: string;
  commanderId?: string;
  groupId?: string;
  controlUnitId?: string;
}
/**
 * Get and fetch control unit from specified device
 * @param deviceId
 * @returns [controlUnits, loading, error]
 *
 * @usage
´´´ts
const [controlUnits, loading, error] = useControlUnitsByIds("UUUID_DEVICE")
´´´
 */
export function useControlUnitsByIds(
  props: Props,
  onError?: (error: unknown) => unknown
) {
  const {
    storeId = props.groupId ?? '',
    deviceId = fromBase64(props.deviceId ?? ''),
    marketId,
  } = useParams();

  const {
    data: device,
    isLoading: isLoadingDevice,
    error: gettingDeviceError,
  } = useGetDeviceFromStoreByParam({
    storeId,
    deviceId,
  });

  const devices: (DevicesStoreInfoResponse | undefined)[] = [device];
  const { isFetching, isLoading } = LocationAPI.useGetLocationsQuery(marketId);
  const devicesLoading = isFetching || isLoading || isLoadingDevice;

  const devicesControlUnits = useAppSelector(
    (state: RootState) =>
      getZipControlUnitsByDeviceIds(state, {
        devices: devices.filter(
          (device) => device !== undefined
        ) as DevicesStoreInfoResponse[],
        workspaceId: marketId,
      }),
    isEqual
  );

  const [refetchCounter, setRefetchCounter] = useState(0);
  const refetch = useCallback(() => {
    setRefetchCounter((c) => c + 1);
  }, []);

  const cachedDevicesControlUnits = useMemo(
    () =>
      devicesControlUnits
        .map((d) => `${d.deviceId}-${d.commanderId}`)
        .join(','),
    [devicesControlUnits]
  );

  const queryArgs: ControlUnitQueryArg[] = useMemo(
    () =>
      devicesControlUnits.map((d) => ({
        deviceId: d.deviceId,
        commanderId: d.commanderId,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cachedDevicesControlUnits, refetchCounter]
  );

  const controlUnitsQuery = useMultipleQueries(
    ControlUnitAPI.endpoints.getControlUnitsByDevice,
    queryArgs,
    { skip: !queryArgs.length }
  );

  return {
    devicesControlUnits,
    isLoading:
      controlUnitsQuery.isLoading ||
      controlUnitsQuery.isFetching ||
      devicesLoading,
    isError: controlUnitsQuery.isError || Boolean(gettingDeviceError),
    refetch,
  };
}
